import {
  YES_NO,
  applicantSection,
  feeInfo,
  feeProofAttachment,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import {
  PRD,
  VEHICLE_ORGAN,
  allOf,
  coOwnersFields,
  every,
  vehicleFields,
} from "modules/ebiForms/definitions/sharedVehiclesConstruction";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * F3. Prawo jazdy (wydanie, wymiana, wtórnik) albo wtórnik dowodu rejestracyjnego / tablic / dodatkowa tablica.
 * Prawo jazdy: art. 10, 11 i 18 ustawy o kierujących pojazdami, § 5 rozporządzenia MI z 5.08.2025 r. (Dz. U. poz. 1073);
 * układ jak wzór z załącznika do tego rozporządzenia (części A, B, C, E, F, G i oświadczenie o miejscu zamieszkania),
 * https://api.sejm.gov.pl/eli/acts/DU/2025/1073/text.pdf — pobrany 2026-10-22 (część wzoru odczytana z rozsypanego
 * tekstu PDF). Pominięto rzadkie pozycje wzoru: zwrot zatrzymanego i przywrócenie cofniętego uprawnienia, międzynarodowe
 * prawo jazdy, pozwolenie na kierowanie tramwajem i część D (potwierdzenie kwalifikacji zawodowej).
 * Pojazd: art. 73 ust. 1b i art. 74a ustawy Prawo o ruchu drogowym; wzory z załączników nr 8 (wtórnik/nowy dowód,
 * wtórnik tablic, tablice z nowym numerem) i nr 4 (dodatkowa tablica na bagażnik) do rozporządzenia MI z 8.11.2024 r.
 * (Dz. U. poz. 1709) — pobrane 2026-10-22; przyczyny utraty i treść oświadczenia o utracie jak wzór Starostwa w Tczewie
 * (https://bip.powiat.tczew.pl/files/file_add/download/6201_formularz-w-o-wyd-wt-dow-rej-tablic-rej-audyt.docx — pobrany).
 * Pełnomocnik tylko w sprawach pojazdu (prawo jazdy wymaga fotografii i podpisu wnioskodawcy).
 */
const LICENCE: EbiCondition = { field: "rodzajSprawy", in: ["prawoJazdyWydanie", "prawoJazdyWymiana"] };
const ISSUE: EbiCondition = { field: "rodzajSprawy", equals: "prawoJazdyWydanie" };
const EXCHANGE: EbiCondition = { field: "rodzajSprawy", equals: "prawoJazdyWymiana" };
const VEHICLE: EbiCondition = { field: "rodzajSprawy", in: ["dowodRejestracyjny", "tablice", "tablicaDodatkowa"] };
const DOCUMENT: EbiCondition = { field: "rodzajSprawy", equals: "dowodRejestracyjny" };
const PLATES: EbiCondition = { field: "rodzajSprawy", equals: "tablice" };
const EXTRA_PLATE: EbiCondition = { field: "rodzajSprawy", equals: "tablicaDodatkowa" };

const HAS_LICENCE: EbiCondition = { field: "posiadaPrawoJazdy", equals: "tak" };
const PROFESSIONAL: EbiCondition = {
  any: ["C1", "C1E", "C", "CE", "D1", "D1E", "D", "DE"].map((category) => ({ field: "kategorie", includes: category })),
};
const MEDICAL_RENEWAL = every(EXCHANGE, { field: "powodWymiany", equals: "termin" }, { field: "badaniaWymagane", equals: "tak" });
const LICENCE_LOST = every(EXCHANGE, { field: "powodWymiany", equals: "utrata" });

const DUPLICATE_DOCUMENT = every(DOCUMENT, { field: "dowodWniosek", in: ["wtornikDowodu", "wtornikPozwolenia"] });
const LOST = ["zgubienie", "kradziez"];
const DOCUMENT_LOST = every(DUPLICATE_DOCUMENT, { field: "dowodPowod", in: LOST });
const PLATES_LOST = every(PLATES, { field: "tablicePowod", in: LOST });
const EXTRA_LOST = every(EXTRA_PLATE, { field: "dodatkowaWniosek", equals: "wtornik" }, { field: "dodatkowaPowod", equals: "utrata" });
const STOLEN: EbiCondition = {
  any: [every(DUPLICATE_DOCUMENT, { field: "dowodPowod", equals: "kradziez" }), every(PLATES, { field: "tablicePowod", equals: "kradziez" })],
};

const LOSS_REASONS = [
  { value: "zgubienie", label: "zgubienia" },
  { value: "kradziez", label: "kradzieży" },
  { value: "zniszczenie", label: "zniszczenia (w stopniu powodującym nieczytelność)" },
];

const CATEGORIES = ["AM", "A1", "A2", "A", "B1", "B", "B+E", "C1", "C1+E", "C", "C+E", "D1", "D1+E", "D", "D+E", "T"].map((label) => ({
  value: label.replace("+", ""),
  label,
}));

const F3: IEbiFormDefinition = {
  code: "F3",
  version: 1,
  title: "Wniosek o prawo jazdy albo wtórnik dowodu rejestracyjnego lub tablic",
  category: EEbiFormCategory.Vehicles,
  office: EEbiFormOffice.County,
  description:
    "Wydanie lub wymiana prawa jazdy (także wtórnik), wtórnik albo nowy dowód rejestracyjny, wtórnik tablic, tablice z nowym numerem i dodatkowa tablica na bagażnik.",
  keywords: ["prawo jazdy", "wymiana prawa jazdy", "wtórnik", "dowód rejestracyjny", "tablice rejestracyjne", "bagażnik", "PKK"],
  organ: `${VEHICLE_ORGAN}, właściwy ze względu na miejsce zamieszkania (pojazd — także siedzibę właściciela)`,
  legalBasis:
    "art. 10, art. 11 i art. 18 ustawy z dnia 5 stycznia 2011 r. o kierujących pojazdami oraz § 5 rozporządzenia Ministra Infrastruktury z dnia 5 sierpnia 2025 r. (Dz. U. poz. 1073) — prawo jazdy; " +
    `art. 73 ust. 1b i art. 74a ${PRD} oraz § 8 i § 13 rozporządzenia Ministra Infrastruktury z dnia 8 listopada 2024 r. (Dz. U. poz. 1709) — dokumenty i tablice pojazdu`,
  request: "Wnoszę o wydanie dokumentu albo tablic rejestracyjnych wskazanych we wniosku.",
  feeLabel: "wg cennika",
  fee: "Prawo jazdy — 100 zł; wtórnik dowodu rejestracyjnego — 54 zł (pozwolenie czasowe — 13,50 zł); wtórnik tablic samochodowych — 80 zł i 12,50 zł za nalepki (jedna tablica — 40 zł i 12,50 zł); dodatkowa tablica na bagażnik — 40 zł i 12,50 zł (praktyka urzędów); pełnomocnictwo — 17 zł.",
  deadlineLabel: "do miesiąca",
  deadline:
    "Profil kandydata na kierowcę — do 2 dni roboczych od złożenia wniosku (§ 5 ust. 2 rozporządzenia z 5 sierpnia 2025 r.); pozostałe sprawy — według KPA.",
  variantField: "rodzajSprawy",
  variants: {
    prawoJazdyWydanie: { title: "Wniosek o wydanie prawa jazdy" },
    prawoJazdyWymiana: { title: "Wniosek o wymianę prawa jazdy albo wydanie wtórnika" },
    dowodRejestracyjny: { title: "Wniosek o wydanie wtórnika lub nowego dowodu rejestracyjnego" },
    tablice: { title: "Wniosek o wydanie wtórnika tablic rejestracyjnych lub tablic z nowym numerem" },
    tablicaDodatkowa: { title: "Wniosek o wydanie dodatkowej tablicy rejestracyjnej do oznaczenia bagażnika" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj sprawy",
      fields: [
        {
          code: "rodzajSprawy",
          type: "radio",
          label: "Wnoszę o",
          required: true,
          options: [
            { value: "prawoJazdyWydanie", label: "wydanie prawa jazdy (pierwsze uprawnienie albo kolejna kategoria)" },
            { value: "prawoJazdyWymiana", label: "wymianę prawa jazdy albo wydanie jego wtórnika" },
            { value: "dowodRejestracyjny", label: "wydanie wtórnika lub nowego dowodu rejestracyjnego albo wtórnika pozwolenia czasowego" },
            { value: "tablice", label: "wydanie wtórnika tablic (tablicy) rejestracyjnych albo tablic z nowym numerem" },
            { value: "tablicaDodatkowa", label: "wydanie dodatkowej tablicy rejestracyjnej do oznaczenia bagażnika (albo jej wtórnika)" },
          ],
        },
      ],
    },
    applicantSection(),
    { ...representationSection(), visibleWhen: VEHICLE },
    {
      code: "daneKierowcy",
      title: "A. Dane (uzupełnienie)",
      visibleWhen: LICENCE,
      fields: [
        { code: "miejsceUrodzenia", type: "text", label: "Miejsce urodzenia", required: true, width: "half", maxLength: 100 },
        {
          code: "obywatelstwo",
          type: "text",
          label: "Obywatelstwo",
          required: true,
          width: "half",
          maxLength: 3,
          defaultValue: "PL",
          hint: "Znak wyróżniający kraj — dla obywateli polskich PL.",
        },
      ],
    },
    {
      code: "wydanie",
      title: "C. Prawo jazdy — wydanie",
      visibleWhen: ISSUE,
      fields: [
        { code: "kategorie", type: "checkboxes", label: "Ubiegam się o prawo jazdy kategorii", required: true, options: CATEGORIES },
        {
          code: "posiadaPrawoJazdy",
          type: "radio",
          label: "Czy masz już prawo jazdy?",
          required: true,
          options: YES_NO,
        },
        {
          code: "niepelnoletni",
          type: "radio",
          label: "Czy w dniu złożenia wniosku nie masz ukończonych 18 lat?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
      ],
    },
    {
      code: "wydanyDokument",
      title: "B. Wydany dokument",
      visibleWhen: { any: [EXCHANGE, every(ISSUE, HAS_LICENCE)] },
      fields: [
        { code: "dokumentKategorie", type: "text", label: "Prawo jazdy — kategorie", required: true, width: "half", maxLength: 60 },
        { code: "dokumentNumer", type: "text", label: "Numer prawa jazdy (numer druku)", required: true, width: "half", maxLength: 40 },
        { code: "dokumentData", type: "date", label: "Data wydania", width: "half" },
        { code: "dokumentOrgan", type: "text", label: "Organ wydający", width: "half", maxLength: 200 },
      ],
    },
    {
      code: "wymiana",
      title: "Wymiana prawa jazdy albo wtórnik",
      visibleWhen: EXCHANGE,
      fields: [
        {
          code: "powodWymiany",
          type: "radio",
          label: "Ubiegam się o",
          required: true,
          options: [
            { value: "termin", label: "wymianę prawa jazdy z powodu upływu terminu jego ważności" },
            { value: "zmianaDanych", label: "wydanie prawa jazdy z powodu zmiany danych" },
            { value: "utrata", label: "wydanie wtórnika z powodu utraty dokumentu" },
            { value: "zniszczenie", label: "wydanie wtórnika z powodu zniszczenia dokumentu w stopniu powodującym jego nieczytelność" },
            { value: "zagraniczne", label: "wymianę prawa jazdy wydanego za granicą" },
            { value: "wojskowe", label: "wymianę wojskowego dokumentu stwierdzającego uprawnienia do kierowania pojazdem" },
          ],
        },
        {
          code: "zmianaZ",
          type: "text",
          label: "Dane dotychczasowe (z)",
          required: true,
          visibleWhen: { field: "powodWymiany", equals: "zmianaDanych" },
          width: "half",
          maxLength: 200,
        },
        {
          code: "zmianaNa",
          type: "text",
          label: "Dane nowe (na)",
          required: true,
          visibleWhen: { field: "powodWymiany", equals: "zmianaDanych" },
          width: "half",
          maxLength: 200,
        },
        {
          code: "badaniaWymagane",
          type: "radio",
          label: "Czy termin ważności uprawnienia wynikał z orzeczenia lekarskiego lub psychologicznego (termin badań)?",
          required: true,
          visibleWhen: { field: "powodWymiany", equals: "termin" },
          options: YES_NO,
        },
      ],
    },
    {
      code: "oswiadczeniaKierowcy",
      title: "G. Oświadczenia",
      description:
        "Ja, niżej podpisany, świadomy odpowiedzialności karnej za składanie fałszywych zeznań (art. 233 § 1 ustawy z dnia 6 czerwca 1997 r. — Kodeks karny), oświadczam, iż na dzień złożenia wniosku (zaznacz właściwe):",
      visibleWhen: LICENCE,
      fields: [
        { code: "g1Dane", type: "declaration", label: "podane we wniosku dane są zgodne ze stanem faktycznym", required: true },
        {
          code: "g2Uzaleznienie",
          type: "declaration",
          label: "nie stwierdzono u mnie aktywnej formy uzależnienia od alkoholu lub podobnie działającego środka",
          requiredWhen: ISSUE,
        },
        {
          code: "g3Zakaz",
          type: "declaration",
          label: "nie został orzeczony w stosunku do mnie — prawomocnym wyrokiem sądu — zakaz prowadzenia pojazdów samochodowych",
          requiredWhen: ISSUE,
        },
        {
          code: "g4Zatrzymanie",
          type: "declaration",
          label: "nie mam zatrzymanego prawa jazdy w zakresie uprawnienia do kierowania, o które wnioskuję",
          requiredWhen: ISSUE,
        },
        { code: "g5Cofniecie", type: "declaration", label: "nie mam cofniętego uprawnienia do kierowania pojazdami", requiredWhen: ISSUE },
        {
          code: "g6InnyDokument",
          type: "declaration",
          label: "nie posiadam innego dokumentu stwierdzającego uprawnienie do kierowania pojazdem silnikowym",
        },
        { code: "g7Utrata", type: "declaration", label: "utraciłem (utraciłam) prawo jazdy", required: true, visibleWhen: LICENCE_LOST },
        {
          code: "g8PrzewozDrogowy",
          type: "declaration",
          label:
            "wykonuję przewóz drogowy różnymi pojazdami, dla których jest wymagane posiadanie prawa jazdy co najmniej jednej spośród kategorii C1, C1+E, C lub C+E oraz D1, D1+E, D lub D+E",
        },
        {
          code: "miejsceZamieszkania",
          type: "radio",
          label:
            "Oświadczenie o miejscu zamieszkania — uprzedzony (uprzedzona) o odpowiedzialności karnej za zeznanie nieprawdy lub zatajenie prawdy (art. 233 § 1 Kodeksu karnego) oświadczam, że moje miejsce zamieszkania znajduje się na terytorium Rzeczypospolitej Polskiej, przy czym:",
          required: true,
          options: [
            {
              value: "a1",
              label: "przebywam w Polsce co najmniej przez 185 dni w każdym roku kalendarzowym ze względu na swoje więzi osobiste i zawodowe",
            },
            {
              value: "a2",
              label:
                "przebywam w Polsce co najmniej przez 185 dni w każdym roku kalendarzowym z zamiarem stałego pobytu wyłącznie ze względu na swoje więzi osobiste",
            },
            {
              value: "b",
              label:
                "przebywam regularnie w Polsce ze względu na swoje więzi osobiste, a ze względu na więzi zawodowe kolejno przebywam w co najmniej dwóch państwach członkowskich Unii Europejskiej",
            },
            {
              value: "c",
              label:
                "przebywam nieregularnie w Polsce ze względu na swoje więzi osobiste, ponieważ przebywam w innym państwie członkowskim Unii Europejskiej w celu wypełniania zadania o określonym czasie trwania",
            },
            { value: "d", label: "przebywam na terytorium innego państwa ze względu na podjęte w tym państwie studia lub naukę w szkole" },
          ],
        },
      ],
    },
    {
      code: "przekazanie",
      title: "F. Sposób przekazania dokumentu i opłata",
      visibleWhen: LICENCE,
      fields: [
        {
          code: "przekazanieDokumentu",
          type: "radio",
          label: "Wnioskowany dokument",
          required: true,
          defaultValue: "osobiscie",
          options: [
            { value: "osobiscie", label: "odbiorę osobiście" },
            { value: "poczta", label: "proszę przesłać za pośrednictwem poczty" },
          ],
        },
        {
          code: "oplataPrawoJazdy",
          type: "fee",
          label: "Opłata za wydanie prawa jazdy",
          feeAmount: 100,
          hint: "Dołącz potwierdzenie przelewu na rachunek urzędu.",
        },
      ],
    },
    {
      code: "pojazd",
      title: "Pojazd",
      visibleWhen: VEHICLE,
      fields: [...vehicleFields(), ...coOwnersFields()],
    },
    {
      code: "dowod",
      title: "Dowód rejestracyjny",
      visibleWhen: DOCUMENT,
      fields: [
        {
          code: "dowodWniosek",
          type: "radio",
          label: "Wnoszę o wydanie",
          required: true,
          options: [
            { value: "wtornikDowodu", label: "wtórnika dowodu rejestracyjnego" },
            { value: "wtornikPozwolenia", label: "wtórnika pozwolenia czasowego" },
            { value: "nowyDowod", label: "nowego dowodu rejestracyjnego" },
          ],
        },
        { code: "dowodPowod", type: "radio", label: "Z powodu", required: true, visibleWhen: DUPLICATE_DOCUMENT, options: LOSS_REASONS },
        {
          code: "dowodDataUtraty",
          type: "date",
          label: "Data utraty (lub najprawdopodobniej)",
          required: true,
          visibleWhen: DOCUMENT_LOST,
          width: "half",
        },
        {
          code: "dowodPowodOpis",
          type: "text",
          label: "Z powodu",
          required: true,
          visibleWhen: allOf(DOCUMENT, { field: "dowodWniosek", equals: "nowyDowod" }),
          maxLength: 300,
          placeholder: "np. zmiana danych właściciela, brak miejsca na wpis terminu badania technicznego",
        },
        {
          code: "badanieCep",
          type: "radio",
          label: "Czy termin następnego badania technicznego pojazdu jest odnotowany w centralnej ewidencji pojazdów?",
          required: true,
          visibleWhen: allOf(DOCUMENT, { field: "dowodWniosek", equals: "wtornikDowodu" }),
          options: YES_NO,
        },
        {
          code: "pozwolenieCzasowe",
          type: "checkbox",
          label: "Wnoszę o wydanie pozwolenia czasowego na okres konieczny do wydania wtórnika dowodu rejestracyjnego / nowego dowodu rejestracyjnego",
        },
      ],
    },
    {
      code: "tablice",
      title: "Tablice rejestracyjne",
      visibleWhen: PLATES,
      fields: [
        {
          code: "tabliceWniosek",
          type: "radio",
          label: "Wnoszę o wydanie",
          required: true,
          options: [
            { value: "wtornik", label: "wtórnika tablic (tablicy) rejestracyjnych z dotychczasowym numerem" },
            { value: "nowyNumer", label: "zalegalizowanych tablic (tablicy) rejestracyjnych z nowym numerem rejestracyjnym" },
          ],
        },
        {
          code: "tabliceLiczba",
          type: "radio",
          label: "Liczba tablic",
          required: true,
          visibleWhen: { field: "tabliceWniosek", equals: "wtornik" },
          options: [
            { value: "1", label: "1 tablica" },
            { value: "2", label: "2 tablice" },
          ],
        },
        {
          code: "tablicePowod",
          type: "radio",
          label: "Z powodu",
          required: true,
          options: [...LOSS_REASONS, { value: "inny", label: "innego (podaj)" }],
        },
        {
          code: "tablicePowodOpis",
          type: "text",
          label: "Inny powód",
          required: true,
          visibleWhen: { field: "tablicePowod", equals: "inny" },
          maxLength: 300,
        },
        { code: "tabliceDataUtraty", type: "date", label: "Data utraty (lub najprawdopodobniej)", required: true, visibleWhen: PLATES_LOST, width: "half" },
        {
          code: "tabliceTymczasowe",
          type: "checkbox",
          label:
            "Wnoszę o wydanie pozwolenia czasowego oraz tymczasowych tablic rejestracyjnych (tymczasowej tablicy) na okres konieczny do wykonania wtórnika tablicy rejestracyjnej",
          visibleWhen: { field: "tabliceWniosek", equals: "wtornik" },
        },
      ],
    },
    {
      code: "tablicaDodatkowa",
      title: "Dodatkowa tablica rejestracyjna do oznaczenia bagażnika zakrywającego tylną tablicę",
      visibleWhen: EXTRA_PLATE,
      fields: [
        {
          code: "dodatkowaWniosek",
          type: "radio",
          label: "Wnoszę o wydanie",
          required: true,
          options: [
            { value: "pierwsza", label: "dodatkowej zalegalizowanej tablicy rejestracyjnej" },
            { value: "wtornik", label: "wtórnika dodatkowej zalegalizowanej tablicy rejestracyjnej" },
          ],
        },
        {
          code: "dodatkowaPowod",
          type: "radio",
          label: "Z powodu",
          required: true,
          visibleWhen: { field: "dodatkowaWniosek", equals: "wtornik" },
          options: [
            { value: "utrata", label: "utraty tablicy" },
            { value: "zniszczenie", label: "zniszczenia tablicy — dotychczasową tablicę oddam w urzędzie" },
          ],
        },
      ],
    },
    {
      code: "utrata",
      title: "Oświadczenie o utracie",
      visibleWhen: { any: [DOCUMENT_LOST, PLATES_LOST, EXTRA_LOST] },
      fields: [
        {
          code: "oswiadczenieUtrata",
          type: "declaration",
          label:
            "Oświadczam, że utraciłem (utraciłam) dokument lub tablicę wskazane we wniosku. Jestem świadomy (świadoma) odpowiedzialności karnej za złożenie fałszywego oświadczenia o utracie.",
          required: true,
        },
      ],
    },
    {
      code: "oplataPojazd",
      title: "Opłaty",
      visibleWhen: VEHICLE,
      fields: [
        feeInfo(
          "infoOplataPojazd",
          "Wtórnik albo nowy dowód rejestracyjny — 54 zł (pozwolenie czasowe — 13,50 zł); wtórnik tablic samochodowych — 80 zł i 12,50 zł za nalepki (jedna tablica — 40 zł i 12,50 zł); dodatkowa tablica na bagażnik — 40 zł i 12,50 zł. Zniszczony dowód rejestracyjny albo tablice oddajesz w urzędzie; nowe tablice i dowód odbierasz osobiście.",
        ),
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "fotografia",
      label: "Fotografia 35 × 45 mm",
      hint: "Kolorowa, na jednolitym jasnym tle, wykonana nie wcześniej niż 6 miesięcy przed złożeniem wniosku (§ 5 ust. 1 pkt 3 rozporządzenia).",
      visibleWhen: LICENCE,
      required: true,
    },
    {
      code: "orzeczenieLekarskie",
      label: "Orzeczenie lekarskie o braku przeciwwskazań zdrowotnych do kierowania pojazdami",
      hint: "Nie dołączasz go, jeśli ważne orzeczenie dla wnioskowanej kategorii jest już w aktach ewidencyjnych kierowcy (§ 5 ust. 9 rozporządzenia).",
      visibleWhen: { any: [ISSUE, MEDICAL_RENEWAL] },
      requiredWhen: { any: [every(ISSUE, { field: "posiadaPrawoJazdy", equals: "nie" }), MEDICAL_RENEWAL] },
    },
    {
      code: "orzeczeniePsychologiczne",
      label: "Orzeczenie psychologiczne o braku przeciwwskazań psychologicznych do kierowania pojazdami",
      hint: "Dla kategorii C1, C, D1, D (także z przyczepą) albo gdy jest wymagane przy wymianie.",
      visibleWhen: { any: [every(ISSUE, PROFESSIONAL), MEDICAL_RENEWAL] },
      requiredWhen: every(ISSUE, PROFESSIONAL),
    },
    {
      code: "zgodaRodzica",
      label: "Pisemna zgoda rodzica albo opiekuna",
      hint: "Art. 11 ust. 2 ustawy o kierujących pojazdami.",
      visibleWhen: allOf(ISSUE, { field: "niepelnoletni", equals: "tak" }),
      required: true,
    },
    {
      code: "kopiaPrawaJazdy",
      label: "Kopia posiadanego prawa jazdy (obie strony)",
      hint: "Oryginał oddajesz przy odbiorze nowego dokumentu.",
      visibleWhen: { any: [every(ISSUE, HAS_LICENCE), every(EXCHANGE, { not: { field: "powodWymiany", equals: "utrata" } })] },
      required: true,
    },
    {
      code: "tlumaczenie",
      label: "Tłumaczenie prawa jazdy wydanego za granicą na język polski",
      hint: "Wymagane dla prawa jazdy nieokreślonego w konwencjach o ruchu drogowym — sporządzone lub poświadczone przez tłumacza przysięgłego albo konsula (§ 5 ust. 6 rozporządzenia).",
      visibleWhen: allOf(EXCHANGE, { field: "powodWymiany", equals: "zagraniczne" }),
    },
    {
      code: "badanieTechniczne",
      label: "Zaświadczenie o pozytywnym wyniku ważnego badania technicznego pojazdu",
      hint: "Gdy w centralnej ewidencji pojazdów brak terminu następnego badania (§ 11 rozporządzenia z 8 listopada 2024 r.).",
      visibleWhen: allOf(DOCUMENT, { field: "dowodWniosek", equals: "wtornikDowodu" }, { field: "badanieCep", equals: "nie" }),
      required: true,
    },
    {
      code: "zaswiadczeniePolicji",
      label: "Zaświadczenie policji o zgłoszeniu kradzieży",
      hint: "(praktyka urzędów) — nieobowiązkowo.",
      visibleWhen: STOLEN,
    },
    ...proxyAttachments().map((attachment) => ({ ...attachment, visibleWhen: allOf(VEHICLE, attachment.visibleWhen) })),
    feeProofAttachment("Dowód uiszczenia opłaty", { visibleWhen: { field: "rodzajSprawy", filled: true } }),
    otherAttachments(),
  ],
};

export default F3;
